// Module 09 — state store (state.v1). A hand-rolled ~20-LoC pub/sub Store<S> (no Zustand, no React,
// no mitt) — the ONLY thing the UI subscribes to: last snapshot + presentation state + selectors.
// Snapshots flow worker → applySnapshot; commands + lifecycle flow dispatchCommand/setRunning/
// setSpeed → an injected Transport (a structural subset of the worker's SimWorkerApi). The store
// NEVER imports the worker — boundary keeps deps = domain only; the app (Module 12) wires the seam.
// Subscribers shallow-diff at the call site (no useShallow). No simulation logic lives here.
// Frozen interface: docs/interfaces/state.v1.md · Rule: .claude/rules/worker-state.md
import { asNodeId } from "../domain/index";
import type {
  Snapshot, Command, Layer, FlowSample, ProsumerRuntime, LoadRuntime,
} from "../domain/index";

// ── public types ───────────────────────────────────────────────────────────────
export interface UiState {
  latest?: Snapshot;
  selection?: { kind: "node" | "edge" | "gen" | "load"; id: string };
  layersVisible: Record<Layer, boolean>;
  clock: { hour: number; speed: number; scenarioId: string };
  running: boolean;
  dispatchCommand(cmd: Command): void;
  applySnapshot(s: Snapshot): void;
  select(sel: UiState["selection"]): void;
  setRunning(running: boolean): void;
  setSpeed(speed: number): void;
}

export interface Store<S> {
  getState(): Readonly<S>;
  setState(patch: Partial<S> | ((s: Readonly<S>) => Partial<S>)): void;
  subscribe(listener: (s: Readonly<S>) => void): () => void;
}

// The transport sink the store forwards to. SimWorkerApi is a structural superset (the app passes its
// worker client directly). Defined here — NOT imported from worker — to keep deps = domain only.
export interface Transport {
  postCommand(cmd: Command): void;
  start(speed: number): void;
  stop(): void;
  setSpeed(speed: number): void;
}

// ── the hand-rolled store ────────────────────────────────────────────────────────
function createStore<S>(initial: S): Store<S> {
  let state = initial;
  const listeners = new Set<(s: Readonly<S>) => void>();
  return {
    getState: () => state,
    setState(patch) {
      const part = typeof patch === "function" ? patch(state) : patch;
      state = { ...state, ...part };
      // F6.6: isolate each subscriber — a throwing listener must NOT abort the notify loop and starve every
      // later-registered subscriber (observed live: a broken recorder starved the next one). Order + synchronous
      // fan-out are preserved; a thrown error is logged and the remaining subscribers still receive the snapshot.
      for (const l of listeners) {
        try { l(state); } catch (e) { console.error("[state] subscriber threw during setState notify", e); }
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  };
}

export function makeSimStore(): { store: Store<UiState>; setTransport(t: Transport | null): void } {
  let transport: Transport | null = null;

  // data-only seed; the action closures (which need `store`) are patched in immediately below.
  const seed = {
    // Finance (money-transfer arcs/settlement) starts OFF — it's the busiest overlay and most users
    // want the grid/energy view first; the control panel's layer toggle lets anyone switch it on.
    layersVisible: { energy: true, information: true, finance: false },
    clock: { hour: 0, speed: 1, scenarioId: "" },
    running: false,
  } as UiState;
  const store = createStore<UiState>(seed);

  const dispatchCommand = (cmd: Command): void => {
    switch (cmd.t) {
      case "setClock":
        store.setState((s) => ({ clock: { ...s.clock, hour: cmd.hour, speed: cmd.speed } }));
        break;
      case "setScenario":
        store.setState((s) => ({ clock: { ...s.clock, scenarioId: cmd.scenarioId } }));
        break;
      case "setLayerVisible":
        store.setState((s) => ({ layersVisible: { ...s.layersVisible, [cmd.layer]: cmd.visible } }));
        break;
      default:
        break; // toggleNode / injectFault / clearFault: no presentation mirror, just forward
    }
    transport?.postCommand(cmd);
  };

  const applySnapshot = (snap: Snapshot): void => {
    store.setState((s) => ({
      latest: snap,
      clock: { ...s.clock, hour: snap.scenario.hour, scenarioId: snap.scenario.id },
    }));
  };

  const select = (sel: UiState["selection"]): void => {
    store.setState({ selection: sel } as Partial<UiState>);
  };

  const setRunning = (running: boolean): void => {
    store.setState({ running });
    if (running) transport?.start(store.getState().clock.speed);
    else transport?.stop();
  };

  const setSpeed = (speed: number): void => {
    store.setState((s) => ({ clock: { ...s.clock, speed } }));
    transport?.setSpeed(speed);
  };

  store.setState({ dispatchCommand, applySnapshot, select, setRunning, setSpeed });

  return { store, setTransport: (t) => { transport = t; } };
}

const _default = makeSimStore();
export const sim: Store<UiState> = _default.store;
export const setTransport = _default.setTransport;

// ── selectors (pure; reference-stable while `latest` is unchanged) ────────────────
const EMPTY_FLOWS: readonly FlowSample[] = [];
const EMPTY_PROSUMERS: readonly ProsumerRuntime[] = [];
const EMPTY_LOADS: readonly LoadRuntime[] = [];

export const selTotals = (s: UiState): Snapshot["totals"] | undefined => s.latest?.totals;

// per-layer filtered flows, memoized per snapshot (WeakMap keyed by `latest`) so repeated calls
// between ticks return the same array reference (subscriber shallow-diff stays quiet).
const flowCache = new WeakMap<Snapshot, Map<Layer, readonly FlowSample[]>>();
export const selFlows = (layer: Layer) => (s: UiState): readonly FlowSample[] => {
  const snap = s.latest;
  if (!snap) return EMPTY_FLOWS;
  let perLayer = flowCache.get(snap);
  if (!perLayer) {
    perLayer = new Map();
    flowCache.set(snap, perLayer);
  }
  const cached = perLayer.get(layer);
  if (cached) return cached;
  const arr = snap.flows.filter((f) => f.layer === layer);
  perLayer.set(layer, arr);
  return arr;
};

export const selProsumers = (s: UiState): readonly ProsumerRuntime[] => s.latest?.prosumers ?? EMPTY_PROSUMERS;
export const selLoads = (s: UiState): readonly LoadRuntime[] => s.latest?.loads ?? EMPTY_LOADS;

export const selSelectionDetail = (s: UiState): unknown => {
  const sel = s.selection;
  const snap = s.latest;
  if (!sel || !snap) return undefined;
  switch (sel.kind) {
    case "load":
      return snap.loads?.find((l) => (l.id as string) === sel.id);
    case "gen":
      return snap.dispatch.find((d) => (d.genId as string) === sel.id);
    case "edge":
      return snap.branchFlows.find((b) => (b.edgeId as string) === sel.id);
    case "node": {
      const angle = snap.nodeAngles.get(asNodeId(sel.id));
      return angle === undefined ? undefined : { id: sel.id, angle };
    }
  }
};
